// Plan 09-02 / Plan 17-07 — Privacy Policy page (NY-09-PRIVACY-COPY).
//
// Bilingual GDPR Art. 13/14-aware privacy policy. Aggregates 24 `privacy.*`
// i18n keys (12 sections × title+body) per CONTEXT.md + RESEARCH.md
// Pattern 5.
//
// Plan 17-07 re-skin: `rg-screen legal` shell + display-word title + back
// Btn (`legal.back` ariaLabel) + scrollable .card body. Section markup
// preserved verbatim — only visual literals tokenized.
//
// Anti-Pattern lock (RESEARCH §410): the user-visible "What we collect" list
// MUST NOT include `deleted_at` — internal column names stay internal; the
// policy says "we delete derived data within 30 days".
//
// DEC-015 / DEC-014 locks: org# TBD until Phase 10 P10-DEF; real legal review
// deferred to Phase 10 P10-DEF-PRIVACY-LEGAL-REVIEW.

import type { JSX, ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { useDocumentTitle } from '../../i18n/useDocumentTitle.js'
import { navigateTo } from '../../router/nav.js'
import { Btn } from '../components/Btn.js'

const CONTAINER_STYLE = {
  width: '100%',
  maxWidth: '480px',
} as const

const SECTION_TITLE_STYLE = {
  fontFamily: 'var(--font-family-display)',
  fontWeight: 600,
  fontSize: 'var(--font-size-20, 20px)',
  margin: '0 0 var(--space-2) 0',
  color: 'var(--color-text-primary)',
} as const

const BODY_STYLE = {
  fontFamily: 'var(--font-family-body)',
  fontWeight: 500,
  fontSize: 'var(--font-size-15, 15px)',
  lineHeight: 1.6,
  margin: 0,
  color: 'var(--color-text-primary)',
} as const

interface SectionProps {
  ariaLabel: string
  titleId: string
  titleDefault: string
  bodyId: string
  bodyDefault: string
  values?: Record<string, ReactNode | ((chunks: ReactNode) => ReactNode)>
}

function Section(props: SectionProps): JSX.Element {
  // Conditional `values` spread — `exactOptionalPropertyTypes: true` forbids
  // passing `undefined` to a prop typed as `Record<...>` without `| undefined`.
  // Spreading the `values` prop only when defined sidesteps the constraint
  // without widening the FormattedMessage type contract.
  const valuesProp = props.values ? { values: props.values } : {}
  return (
    // Folk parity: each policy section is a borderless near-white folk card
    // (.card recipe). aria-label is preserved verbatim on the <section>.
    <section
      className="card"
      aria-label={props.ariaLabel}
      style={CONTAINER_STYLE}
    >
      <h2 style={SECTION_TITLE_STYLE}>
        <FormattedMessage
          id={props.titleId}
          defaultMessage={props.titleDefault}
        />
      </h2>
      <p style={BODY_STYLE}>
        <FormattedMessage
          id={props.bodyId}
          defaultMessage={props.bodyDefault}
          {...valuesProp}
        />
      </p>
    </section>
  )
}

export function PrivacyPolicy(): JSX.Element {
  useDocumentTitle('doc.title.privacy')
  const intl = useIntl()
  const backLabel = intl.formatMessage({
    id: 'legal.back',
    defaultMessage: 'Back',
  })
  const emailLink = (
    <a
      href="mailto:admin@arcticmill.com"
      style={{ color: 'var(--color-cta-primary)' }}
    >
      admin@arcticmill.com
    </a>
  )

  return (
    <main
      id="main"
      tabIndex={-1}
      className="rg-screen legal"
      data-screen="privacy"
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--color-bg-mid)',
        padding: 'var(--space-6) var(--space-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-5)',
        alignItems: 'center',
        color: 'var(--color-text-primary)',
      }}
    >
      <header
        style={{
          width: '100%',
          maxWidth: '480px',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
        }}
      >
        <Btn
          kind="ghost"
          size="sm"
          onClick={() => navigateTo('/about')}
          ariaLabel={backLabel}
          testId="legal-back"
        >
          ←
        </Btn>
        <h1
          className="display-word"
          style={{ fontSize: 'var(--font-size-22)', margin: 0 }}
        >
          <FormattedMessage
            id="privacy.title"
            defaultMessage="Privacy Policy"
          />
        </h1>
      </header>

      {/* Section 1: Controller (Arcmill, admin@arcticmill.com, org# TBD-Phase-10) */}
      <Section
        ariaLabel="Data controller"
        titleId="privacy.controller.title"
        titleDefault="Data controller"
        bodyId="privacy.controller.body"
        bodyDefault="Data controller: Arcmill (organization number TBD). Contact: {email}."
        values={{ email: emailLink }}
      />

      {/* Section 2: Purposes of processing */}
      <Section
        ariaLabel="Purposes of processing"
        titleId="privacy.purposes.title"
        titleDefault="Purposes of processing"
        bodyId="privacy.purposes.body"
        bodyDefault="We process your data to operate the game (legitimate interest under GDPR Art. 6(1)(f)) and to send push notifications when you opt in (consent under GDPR Art. 6(1)(a))."
      />

      {/* Section 3: Legal basis (Art. 6(1)(f) + Art. 6(1)(a)) */}
      <Section
        ariaLabel="Legal basis"
        titleId="privacy.legal_basis.title"
        titleDefault="Legal basis"
        bodyId="privacy.legal_basis.body"
        bodyDefault="Account and match data: legitimate interest (Art. 6(1)(f)) — necessary to provide the game. Push notifications: consent (Art. 6(1)(a)) — you can withdraw at any time in Settings."
      />

      {/* Section 4: Recipients / sub-processors (Cloudflare EU + SCCs, Sentry, Plausible) */}
      <Section
        ariaLabel="Recipients and sub-processors"
        titleId="privacy.recipients.title"
        titleDefault="Recipients and sub-processors"
        bodyId="privacy.recipients.body"
        bodyDefault="We use Cloudflare Inc. (EU primary region, with Standard Contractual Clauses for any cross-border transfer), Sentry for error tracking, and Plausible for aggregate analytics. No data is sold or shared with advertisers."
      />

      {/* Section 5: International transfers */}
      <Section
        ariaLabel="International transfers"
        titleId="privacy.transfers.title"
        titleDefault="International transfers"
        bodyId="privacy.transfers.body"
        bodyDefault="Data is stored in Cloudflare's EU primary region. Any cross-border transfer is covered by Standard Contractual Clauses (SCCs)."
      />

      {/* Section 6: Retention (30 days post-deletion) */}
      <Section
        ariaLabel="Data retention"
        titleId="privacy.retention.title"
        titleDefault="Data retention"
        bodyId="privacy.retention.body"
        bodyDefault="Account-derived data is deleted within 30 days of account deletion. Replays are retained for 365 days unless flagged."
      />

      {/* Section 7: Data subject rights (Art. 15/16/17/18/20/21) */}
      <Section
        ariaLabel="Your data rights"
        titleId="privacy.rights.title"
        titleDefault="Your data rights"
        bodyId="privacy.rights.body"
        bodyDefault="Under GDPR you have the right to access (Art. 15), rectify (Art. 16), erase (Art. 17), restrict (Art. 18), portability (Art. 20), and object (Art. 21). To exercise any of these, contact {email}."
        values={{ email: emailLink }}
      />

      {/* Section 8: Right to lodge a complaint (Swedish IMY) */}
      <Section
        ariaLabel="Right to complain"
        titleId="privacy.complaint.title"
        titleDefault="Right to complain"
        bodyId="privacy.complaint.body"
        bodyDefault="You may lodge a complaint with your local data protection authority. In Sweden, this is Integritetsskyddsmyndigheten (IMY)."
      />

      {/* Section 9: Children's notice (COPPA — 13+) */}
      <Section
        ariaLabel="Children's notice"
        titleId="privacy.children.title"
        titleDefault="Children's notice"
        bodyId="privacy.children.body"
        bodyDefault="This service is intended for users 13 years of age and older. We do not knowingly collect data from children under 13. We do not collect real names, ages, or birthdates."
      />

      {/* Section 10: Explicit collection list (DEC-019 GDPR-minimum;
          ANTI-PATTERN: do NOT expose `deleted_at` per RESEARCH §410). */}
      <Section
        ariaLabel="What we collect"
        titleId="privacy.collected.title"
        titleDefault="What we collect"
        bodyId="privacy.collected.body"
        bodyDefault="We collect: a randomly-generated user_uuid, your identity-provider kind (Apple or Google), a peppered hash of your provider sub identifier, your display name, your friend code, your locale and preferred language, account timestamps and flags, a push-token hash if you opt in, and hashed analytics identifiers only."
      />

      {/* Section 11: Explicit NOT-collected list (DEC-019) */}
      <Section
        ariaLabel="What we do NOT collect"
        titleId="privacy.not_collected.title"
        titleDefault="What we do NOT collect"
        bodyId="privacy.not_collected.body"
        bodyDefault="We do NOT collect: email addresses, real names, IP-derived geolocation, device fingerprints, IDFA, GAID, or any advertising identifier."
      />

      {/* Section 12: Cookies / local storage */}
      <Section
        ariaLabel="Cookies and local storage"
        titleId="privacy.cookies.title"
        titleDefault="Cookies and local storage"
        bodyId="privacy.cookies.body"
        bodyDefault="We use local storage for your authentication bearer, locale preference, motion preference, and onboarding state. We do NOT use advertising cookies or third-party trackers."
      />
    </main>
  )
}
